import { T, Card, SectionHeader, Chip, ScoreRing, Skeleton, SEV_COLOR, AGENT_COLOR, STATUS_COLOR } from '../components/ui';
import type { Alert, AgentState, Retailer, Product } from '../api/client';

interface Props {
 alerts: Alert[]; agents: AgentState[]; retailers: Retailer[];
 products: Product[]; heartbeat: { agentsActive: number; openAlerts: number; uptime: number } | null;
}

export default function DashboardPage({ alerts, agents, retailers, products, heartbeat }: Props) {
 const unread = alerts.filter(a => a.status === 'open').length;
 const avgScore = products.length ? Math.round(products.reduce((s, p) => s + p.provenance_score, 0) / products.length) : 0;
 const liveCount = retailers.filter(r => r.status === 'live').length;
 const topProducts = [...products].sort((a, b) => b.provenance_score - a.provenance_score).slice(0, 5);
 const criticalAlerts = alerts.filter(a => a.severity === 'critical' || a.severity === 'high').slice(0, 4);

 const hero = [
 { label: 'Products Tracked', value: products.length.toLocaleString(), color: T.copper },
 { label: 'Avg Provenance', value: avgScore, color: avgScore >= 85 ? T.sage : T.amber, suffix: '/100' },
 { label: 'Live Retailers', value: liveCount, color: T.sapphire },
 { label: 'Open Alerts', value: unread, color: unread > 0 ? T.crimson : T.sage },
 { label: 'Agents Active', value: heartbeat ? heartbeat.agentsActive : agents.filter(a => a.status === 'active').length, color: T.sage },
 { label: 'Chain Integrity', value: '99.7%', color: T.sage },
 ];

 return (
 <div className="fade-up">
 <SectionHeader
 title="Platform Dashboard"
 sub="Real-time supply chain intelligence across all retailers"
 right={<Chip label="Live" color={T.sage} dot />}
 />

 {/* Hero stats */}
 <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12, marginBottom: 24 }}>
 {hero.map(h => (
 <Card key={h.label} style={{ textAlign: 'center', padding: '18px 12px' }}>
 <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: 28, color: h.color, marginBottom: 4 }}>
 {h.value}{h.suffix || ''}
 </div>
 <div style={{ color: T.sub, fontSize: 10, textTransform: 'uppercase', letterSpacing: .8 }}>{h.label}</div>
 </Card>
 ))}
 </div>

 <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
 {/* Agents grid */}
 <Card>
 <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: 16, color: T.text, marginBottom: 16 }}>Agent Network</div>
 <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
 {agents.length === 0
 ? [1,2,3,4,5,6].map(i => <Skeleton key={i} h={64} />)
 : agents.map(a => (
 <div key={a.id} style={{
 background: T.card, border: `1px solid ${T.border}`,
 borderLeft: `3px solid ${AGENT_COLOR[a.agent_type || a.id] || T.sub}`,
 borderRadius: 9, padding: '10px 12px',
 }}>
 <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
 <span style={{ fontSize: 11, fontWeight: 600, color: T.text }}>{a.name}</span>
 <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
 <span style={{ width: 6, height: 6, borderRadius: '50%', background: STATUS_COLOR[a.status] || T.sub,
 animation: a.status === 'active' ? 'pulse 2s infinite' : 'none' }} />
 <span style={{ fontSize: 9, color: T.sub, textTransform: 'uppercase' }}>{a.status}</span>
 </span>
 </div>
 <div style={{ fontSize: 10, color: T.sub, lineHeight: 1.4 }}>{a.current_task || 'Idle'}</div>
 </div>
 ))}
 </div>
 </Card>

 {/* Critical alerts */}
 <Card>
 <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: 16, color: T.text, marginBottom: 16 }}>
 Recent Alerts
 {unread > 0 && <span style={{ marginLeft: 8, fontSize: 10, color: T.crimson, fontFamily: "'DM Sans', sans-serif" }}>{unread} unread</span>}
 </div>
 {criticalAlerts.length === 0
 ? <div style={{ color: T.sub, fontSize: 12, textAlign: 'center', padding: '20px 0' }}>No open alerts</div>
 : criticalAlerts.map(a => (
 <div key={a.id} style={{
 display: 'flex', gap: 10, padding: '10px 0',
 borderBottom: `1px solid ${T.border}`,
 }}>
 <div style={{
 width: 8, height: 8, borderRadius: '50%', flexShrink: 0, marginTop: 3,
 background: SEV_COLOR[a.severity] || T.sub,
 }} />
 <div style={{ flex: 1, minWidth: 0 }}>
 <div style={{ fontSize: 12, color: T.text, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
 {a.title}
 </div>
 <div style={{ fontSize: 10, color: T.sub, marginTop: 2 }}>
 {new Date(a.created_at).toLocaleTimeString()}
 </div>
 </div>
 <Chip label={a.severity} color={SEV_COLOR[a.severity] || T.sub} />
 </div>
 ))}
 </Card>
 </div>

 <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
 {/* Top products */}
 <Card>
 <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: 16, color: T.text, marginBottom: 16 }}>Top Provenance Scores</div>
 {topProducts.map(p => (
 <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
 <span style={{ fontSize: 18 }}>{p.img_emoji}</span>
 <div style={{ flex: 1, minWidth: 0 }}>
 <div style={{ fontSize: 12, color: T.text, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
 <div style={{ fontSize: 10, color: T.sub }}>{p.retailer_name || p.retailer_slug}</div>
 </div>
 <ScoreRing score={p.provenance_score} size={40} />
 </div>
 ))}
 </Card>

 {/* Retailer overview */}
 <Card>
 <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: 16, color: T.text, marginBottom: 16 }}>Retailer Status</div>
 {retailers.length === 0
 ? [1,2,3].map(i => <div key={i} style={{ marginBottom: 12 }}><Skeleton h={48} /></div>)
 : retailers.map(r => {
 const rProducts = products.filter(p => p.retailer_slug === r.slug || p.retailer_id === r.id);
 const rAvg = rProducts.length ? Math.round(rProducts.reduce((s, p) => s + p.provenance_score, 0) / rProducts.length) : 0;
 return (
 <div key={r.id} style={{
 display: 'flex', alignItems: 'center', gap: 12,
 padding: '10px 0', borderBottom: `1px solid ${T.border}`,
 }}>
 <span style={{ fontSize: 18 }}>{r.logo_emoji}</span>
 <div style={{ flex: 1 }}>
 <div style={{ fontSize: 12, color: T.text, fontWeight: 500 }}>{r.name}</div>
 <div style={{ fontSize: 10, color: T.sub }}>{r.product_count.toLocaleString()} products</div>
 </div>
 <Chip label={r.status} color={r.status === 'live' ? T.sage : r.status === 'beta' ? T.amber : T.sub} />
 {rAvg > 0 && <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: rAvg >= 85 ? T.sage : T.amber, fontWeight: 700 }}>{rAvg}</div>}
 </div>
 );
 })}
 </Card>
 </div>
 </div>
 );
}
